import React from 'react'
import { ActivityIndicator, TouchableOpacity, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import moment from 'moment'
import { ImageStyle } from 'react-native-fast-image'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Pill, Text } from '@src/components/base'
import { AuthStackParamList } from '@src/navigation/types'
import { VIDEO_CALL_CARD_DATE_FORMAT, VIDEO_CALL_CARD_TIME_FORMAT } from '@src/config/momentFormat'
import { ImageWithPlaceholder } from '@src/components/Image/FastImageWithPlaceholder'
import { useGoBack } from '@src/utils'
import { UiPillType } from '@src/types'
import Storage, { VideoCallData } from '@src/utils/storage'
import { OnboardingFlowType } from '@src/screens/SignUp/constants'

export const ReferralVideoCallReserved = () => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const goBack = useGoBack()
  const route = useRoute<RouteProp<AuthStackParamList, 'ReferralVideoCallReserved'>>()
  const { params } = route
  const {
    meetingAt,
    coachFullName,
    coachPhoto,
    coachCredentials,
    coachBio,
    coachSpecialties,
    coachUserId,
    coachId,
    referringUserId,
  } = params

  const onPress = () => {
    Storage.set(Storage.ONBOARDING_FLOW_TYPE_SELECTED_KEY, OnboardingFlowType.ReferralVideoCall)
    const videoCallData: VideoCallData = {
      meetingAt,
      coachFullName,
      coachPhoto,
      coachCredentials,
      coachBio,
      coachSpecialties,
      coachId,
      coachUserId,
      referringUserId,
    }
    Storage.set(Storage.VIDEO_CALL_DATA_KEY, videoCallData)

    navigation.navigate('CreateAccountOptionsStelo')
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top + 12 }]}>
      <TouchableOpacity
        onPress={goBack}
        activeOpacity={0.7}
        accessibilityLabel="Go Back"
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <Icon name="caret-left" size="20" weight="bold" />
      </TouchableOpacity>
      <Text type="title-2" style={styles.title}>
        Great!
      </Text>
      <Text type="large" style={styles.coachDescription}>
        Now create your account to confirm your appointment with:
      </Text>
      <View style={styles.coachContainer}>
        {coachPhoto && (
          <ImageWithPlaceholder
            style={styles.coachPhoto as ImageStyle}
            source={{ uri: coachPhoto }}
            placeholderComponent={<ActivityIndicator animating size="small" {...styles.spinner} />}
          />
        )}
        <View style={styles.coachTextContainer}>
          <Text type="large" bold>
            {coachFullName}
          </Text>
          {coachCredentials && (
            <Text type="regular" style={styles.textSecondary}>
              {coachCredentials}
            </Text>
          )}
        </View>
      </View>
      <Text type="small" style={[styles.subtitle, styles.textSecondary]}>
        Slot reserved
      </Text>
      <View style={styles.appointmentCard}>
        <View style={styles.iconContainer}>
          <Icon name="calendar-check" size="24" weight="bold" style={styles.calendarIcon} />
        </View>
        <View style={{ flex: 1 }}>
          <Text type="regular" bold>
            {moment(meetingAt).format(VIDEO_CALL_CARD_DATE_FORMAT)}
          </Text>
          <Text type="small" style={styles.textSecondary}>
            {moment(meetingAt).format(VIDEO_CALL_CARD_TIME_FORMAT)} -{' '}
            {moment(meetingAt).add(60, 'minutes').format(VIDEO_CALL_CARD_TIME_FORMAT)}
          </Text>
        </View>
        <Pill
          style={styles.pill}
          text="Reserved"
          status={UiPillType.Info}
          textBold={false}
          textType="small"
        />
      </View>
      <View style={[styles.buttonContainer, { paddingBottom: insets.bottom + 16 }]}>
        <Text type="large" style={styles.buttonText}>
          You’re almost there! A few more steps to go.
        </Text>
        <Button type="primary" size="block" accessibilityLabel="Create Account" onPress={onPress}>
          Create Account
        </Button>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    backgroundColor: 'theme.background',
    flex: 1,
    paddingHorizontal: 16,
  },
  title: {
    marginBottom: 20,
    textAlign: 'center',
  },
  coachDescription: {
    maxWidth: 290,
    textAlign: 'center',
    alignSelf: 'center',
  },
  subtitle: {
    marginTop: 8,
    marginBottom: 8,
  },
  textSecondary: {
    color: 'theme.text.secondary',
  },
  appointmentCard: {
    borderColor: 'theme.surface.base1',
    borderRadius: 16,
    borderWidth: 2,
    padding: 12,
    marginBottom: 32,
    gap: 16,
    alignItems: 'center',
    flexDirection: 'row',
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'theme.info.lightest',
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarIcon: {
    color: 'theme.info.base',
  },
  pill: {
    height: 30,
    paddingHorizontal: 12,
  },
  buttonContainer: {
    flex: 1,
    gap: 40,
    justifyContent: 'flex-end',
  },
  buttonText: {
    maxWidth: 260,
    textAlign: 'center',
    alignSelf: 'center',
  },
  coachContainer: {
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 40,
    marginBottom: 40,
    gap: 8,
  },
  coachPhoto: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  coachTextContainer: {
    justifyContent: 'space-between',
  },
  spinner: {
    color: 'theme.surface.base',
  },
})
